import {useMemo, useState} from 'react';
import {type TextFieldProps} from '@netx/core-components/TextField';
import {splitEventHandler} from '@netx/core-next/utils';

/**
 * Hook for preventing autofill detection
 * @see https://bugs.chromium.org/p/chromium/issues/detail?id=1405170
 * @example
 * const usernameAutofillHack = useAutofillDetection();
 * const passwordAutofillHack = useAutofillDetection();
 * return (
 *  <LoginTextField
 * 		{...usernameAutofillHack({
 * 			name: 'login',
 * 			label: 'Username',
 * 		})}
 *	/>
 *	<LoginTextField
 * 		{...usernameAutofillHack({
 * 			name: 'password',
 * 			label: 'Password',
 * 			type: 'password',
 * 		})}
 *	/>
 * )
 */
export const useAutofillDetection = () => {
	const [autofillDetected, setAutofillDetected] = useState(false);

	// there is an issue with chrome where autofilled fields, at page load, will not present themselves as
	// having a `value` until the dom is interacted with.
	//
	// we will listen for animation start and if it is `mui-auto-fill` we know that the input was updated
	// at which point we can make sure the field label is shrunk
	// if any further changes are made then all hacks are removed
	// if the document was already focused then all hacks are skipped
	const sendProps = useMemo(() => {
		return <T extends {[key: string]: any}>({
			onAnimationStart,
			onChange,
			InputLabelProps,
			InputProps,
			...props
		}: TextFieldProps & T): TextFieldProps & T =>
			({
				...props,
				onAnimationStart: splitEventHandler(
					onAnimationStart,
					!autofillDetected
						? (event) => {
								if (event.animationName === 'mui-auto-fill')
									setAutofillDetected(true);
						  }
						: undefined,
				),
				onChange: splitEventHandler(
					onChange,
					autofillDetected ? () => setAutofillDetected(false) : undefined,
				),
				InputProps: {
					notched: autofillDetected ? true : undefined,
					...InputProps,
				},
				InputLabelProps: {
					shrink: autofillDetected ? true : undefined,
					...InputLabelProps,
				},
			} as TextFieldProps & T);
	}, [autofillDetected]);

	return sendProps;
};

export default useAutofillDetection;
